import { ChatCompletionService } from "../../services/openai/chatCompletionService.js";

// Create service instance
const chatCompletionService = new ChatCompletionService();

/**
 * Create a new vector store
 * @param {string} name - Name for the vector store
 * @returns {Promise<string>} - The ID of the created vector store
 */
export const createVectorStore = async (name) => {
  try {
    const vectorStore = await chatCompletionService.createVectorStore(name);
    return vectorStore.id;
  } catch (createError) {
    console.error("Error creating vector store:", createError);
    throw new Error(`Failed to create vector store: ${createError.message}`);
  }
};

/**
 * Attach a file to a vector store
 * @param {Object} fileData - Data about the file to attach
 * @param {File} fileData.file - The actual file object (optional if openai_file_id is provided)
 * @param {string} fileData.openai_file_id - Existing OpenAI file ID (optional)
 * @param {string} vectorStoreId - ID of the vector store to attach the file to
 * @returns {Promise<Object>} - Result of the attachment operation
 */
export const attachFileToVectorStore = async (fileData, vectorStoreId) => {
  try {
    if (!vectorStoreId) {
      throw new Error("Vector store ID is required");
    }

    let fileId = fileData.openai_file_id;

    // If we have file content and no existing file ID, use the combined upload-and-attach endpoint
    if (fileData.file && !fileId) {
      // Convert file to base64 for backend transmission
      const buffer = await fileData.file.arrayBuffer();
      const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
      const content = `data:${fileData.file.type};base64,${base64}`;

      // Use the new combined upload-and-attach endpoint
      const response = await chatCompletionService.makeBackendRequest(
        `/vector-store/${vectorStoreId}/files/upload-and-attach`,
        {
          content,
          filename: fileData.file.name,
        }
      );

      return {
        success: true,
        fileId: response.fileUpload?.id,
        vectorStoreFileId: response.uploadResponse?.id,
        response,
      };
    }

    // If we have an existing file ID, use the improved attach endpoint
    if (fileId) {
      const response = await chatCompletionService.makeBackendRequest(
        `/vector-store/${vectorStoreId}/files/attach`,
        {
          file_id: fileId,
        }
      );

      return {
        success: true,
        fileId,
        vectorStoreFileId: response.uploadResponse?.id,
        response,
      };
    }

    throw new Error("Either file content or OpenAI file ID must be provided");
  } catch (error) {
    console.error("Error attaching file to vector store:", error);
    throw error;
  }
};

/**
 * Get vector store details
 * @param {string} vectorStoreId - ID of the vector store
 * @returns {Promise<Object>} - Vector store details
 */
export const getVectorStore = async (vectorStoreId) => {
  try {
    if (!vectorStoreId) {
      throw new Error("Vector store ID is required");
    }

    const vectorStore =
      await chatCompletionService.getVectorStore(vectorStoreId);
    return vectorStore;
  } catch (error) {
    console.error("Error retrieving vector store:", error);
    throw error;
  }
};

/**
 * List all vector stores
 * @param {Object} params - List parameters
 * @returns {Promise<Array>} - Array of vector stores
 */
export const listVectorStores = async (params = {}) => {
  try {
    const vectorStores = await chatCompletionService.listVectorStores(params);
    return vectorStores;
  } catch (error) {
    console.error("Error listing vector stores:", error);
    throw error;
  }
};

/**
 * Delete a vector store
 * @param {string} vectorStoreId - ID of the vector store to delete
 * @returns {Promise<Object>} - Result of the deletion operation
 */
export const deleteVectorStore = async (vectorStoreId) => {
  try {
    if (!vectorStoreId) {
      throw new Error("Vector store ID is required");
    }

    const result = await chatCompletionService.deleteVectorStore(vectorStoreId);
    return result;
  } catch (error) {
    // If vector store doesn't exist (404), treat it as success since the goal is achieved
    if (error.message && error.message.includes("404")) {
      console.log(
        `Vector store ${vectorStoreId} not found (may have been already deleted), continuing...`
      );
      return { success: true, alreadyDeleted: true };
    }
    console.error("Error deleting vector store:", error);
    throw error;
  }
};
